import { useState, useMemo } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import worldCountries110m from 'world-atlas/countries-110m.json'
import {
  countryNameToCode,
  formatDemographicsFollowerShare,
} from '../../../lib/utils/demographicsFollowers'
import './AudienceGeographicDistribution.css'

const DEMOGRAPHIC_EMPTY_MESSAGE = 'Demographic data not available for this platform.'

const WORLD_COUNTRIES_GEO = worldCountries110m

const GEO_NAME_ALIASES = {
  'united states': 'united states of america',
  usa: 'united states of america',
  uk: 'united kingdom',
  'great britain': 'united kingdom',
  uae: 'united arab emirates',
  'czech republic': 'czechia',
  'bosnia and herzegovina': 'bosnia and herz.',
  'north macedonia': 'macedonia',
  'western sahara': 'w. sahara',
  'south sudan': 's. sudan',
  'democratic republic of the congo': 'dem. rep. congo',
  'democratic republic of congo': 'dem. rep. congo',
  'central african republic': 'central african rep.',
  'dominican republic': 'dominican rep.',
  'equatorial guinea': 'eq. guinea',
  'solomon islands': 'solomon is.',
}

const normalizeGeoName = (name) => String(name ?? '').trim().toLowerCase()

/** Atlas uses numeric ISO ids. Build alpha-2 → id from atlas names + Intl, not a hardcoded country list. */
function buildCountryCodeToGeoId(topo) {
  const geometries = topo?.objects?.countries?.geometries ?? []
  const nameToId = {}
  for (const geom of geometries) {
    const id = geom?.id != null ? String(geom.id) : ''
    const name = normalizeGeoName(geom?.properties?.name)
    if (id && name) nameToId[name] = id
  }

  const codeToId = {}
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
    return codeToId
  }
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'region' })
    for (let first = 65; first <= 90; first += 1) {
      for (let second = 65; second <= 90; second += 1) {
        const code = String.fromCharCode(first, second)
        const display = names.of(code)
        if (!display || display === code) continue
        const key = normalizeGeoName(display)
        const geoId = nameToId[key] || nameToId[GEO_NAME_ALIASES[key]]
        if (geoId) codeToId[code] = geoId
      }
    }
  } catch {
    /* name match on the map still works */
  }
  return codeToId
}

const COUNTRY_CODE_TO_ID = buildCountryCodeToGeoId(WORLD_COUNTRIES_GEO)

const countryCodeLabel = (item) => {
  const fromApi = String(item?.code ?? '').trim().toUpperCase()
  if (/^[A-Z]{2}$/.test(fromApi)) return fromApi
  return countryNameToCode(item?.country)
}

const colorScale = (value, min, max) => {
  if (max <= min || value <= 0) return '#e5e7eb'
  const t = Math.min(1, (value - min) / (max - min))
  // Interpolate from #e5e7eb (gray) -> #7ec8e3 (light blue) -> #14b8a6 (teal)
  const r = Math.round(229 - 103 * t - 86 * t * t)
  const g = Math.round(231 - 31 * t - 16 * t * t)
  const b = Math.round(235 - 8 * t - 41 * t * t)
  return `rgb(${Math.max(0, Math.min(255, r))}, ${Math.max(0, Math.min(255, g))}, ${Math.max(0, Math.min(255, b))})`
}

const AudienceGeographicDistribution = ({ data, platform, platformLabel }) => {
  const [hoveredCountry, setHoveredCountry] = useState(null)

  const { countByGeoId, countByGeoName, total, maxCount, minCount } = useMemo(() => {
    if (!data?.geographic?.length) {
      return { countByGeoId: {}, countByGeoName: {}, total: 0, maxCount: 0, minCount: 0 }
    }
    const map = {}
    const byName = {}
    let total = 0
    let maxCount = 0
    let minCount = Infinity
    data.geographic.forEach((item) => {
      const geoId = COUNTRY_CODE_TO_ID[item.code]
      if (geoId) map[geoId] = { ...item, count: item.count }
      const geoName = normalizeGeoName(item.country)
      if (geoName) {
        byName[geoName] = { ...item, count: item.count }
        const alias = GEO_NAME_ALIASES[geoName]
        if (alias) byName[alias] = { ...item, count: item.count }
      }
      total += item.count
      maxCount = Math.max(maxCount, item.count)
      if (item.count > 0) minCount = Math.min(minCount, item.count)
    })
    return {
      countByGeoId: map,
      countByGeoName: byName,
      total,
      maxCount,
      minCount: minCount === Infinity ? 0 : minCount,
    }
  }, [data])

  if (!data || !data.geographic || data.geographic.length === 0) {
    return (
      <div className="audienceGeographicEmpty">
        <div className="audienceGeographicEmptyIcon">🌍</div>
        <p className="audienceGeographicEmptyText">{DEMOGRAPHIC_EMPTY_MESSAGE}</p>
      </div>
    )
  }

  const geographic = data.geographic
  const legendSteps = [
    0,
    Math.round(maxCount * 0.25),
    Math.round(maxCount * 0.5),
    Math.round(maxCount * 0.75),
    maxCount,
  ]

  return (
    <div className="audienceGeographicDistribution">
      <div className="audienceGeographicTable">
        <div className="audienceGeographicTableHeader">
          <div className="audienceGeographicTableCol audienceGeographicTableCol--country">Country</div>
          <div className="audienceGeographicTableCol audienceGeographicTableCol--total">Total Followers</div>
          <div className="audienceGeographicTableCol audienceGeographicTableCol--pct">Follower %</div>
        </div>
        <div className="audienceGeographicTableBody">
          {geographic.map((item) => (
            <div
              key={item.country}
              className="audienceGeographicTableRow"
              onMouseEnter={() => setHoveredCountry(item)}
              onMouseLeave={() => setHoveredCountry(null)}
            >
              <div className="audienceGeographicTableCol audienceGeographicTableCol--country">
                <span className="audienceGeographicFlag">{countryCodeLabel(item) || '—'}</span>
                <span className="audienceGeographicCountryName">{item.country}</span>
              </div>
              <div className="audienceGeographicTableCol audienceGeographicTableCol--total">
                {item.count.toLocaleString()}
              </div>
              <div className="audienceGeographicTableCol audienceGeographicTableCol--pct">
                {formatDemographicsFollowerShare(item.count, total)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="audienceGeographicMap">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 130 }}
          className="audienceGeographicMapSvg"
        >
          <Geographies geography={WORLD_COUNTRIES_GEO}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const geoId = geo.id
                const item =
                  countByGeoId[geoId] ||
                  countByGeoName[normalizeGeoName(geo.properties?.name)]
                const count = item?.count ?? 0
                const fill =
                  count > 0
                    ? colorScale(count, minCount, maxCount)
                    : '#f3f4f6'
                const isHovered = hoveredCountry && item && hoveredCountry.country === item.country
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke={isHovered ? '#871ED6' : '#e5e7eb'}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    onMouseEnter={() => item && setHoveredCountry(item)}
                    onMouseLeave={() => setHoveredCountry(null)}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', cursor: item ? 'pointer' : 'default' },
                      pressed: { outline: 'none' },
                    }}
                  />
                )
              })
            }
          </Geographies>
        </ComposableMap>
        {hoveredCountry && (
          <div className="audienceGeographicTooltip">
            <div className="audienceGeographicTooltipCountry">{hoveredCountry.country}</div>
            <div className="audienceGeographicTooltipPct">
              {formatDemographicsFollowerShare(hoveredCountry.count, total)} of followers
            </div>
            <div className="audienceGeographicTooltipCount">
              {hoveredCountry.count.toLocaleString()} followers
            </div>
          </div>
        )}
        <div className="audienceGeographicLegend">
          <div className="audienceGeographicLegendBar" />
          <div className="audienceGeographicLegendLabels">
            {legendSteps.map((val) => (
              <span key={val}>{val.toLocaleString()}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AudienceGeographicDistribution
